import React, {useEffect, useState, useRef} from 'react'
import PropTypes from 'prop-types'
import {Box} from '@salesforce/retail-react-app/app/components/shared/ui'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getAppOrigin} from '@salesforce/pwa-kit-react-sdk/utils/url'
import {onClient} from '@salesforce/commerce-sdk-react/utils'
import {CookieStorage} from '@salesforce/commerce-sdk-react/auth/storage'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useShopperBasketsMutation, useAccessToken} from '@salesforce/commerce-sdk-react'
import {useCheckout} from '../util/checkout-context'
import {fetchShippingMethods} from '../../../adyen/lib/contexts/adyen-express-checkout-context'
import {paidWithGC} from '../../../api/utils/gcHelpers'
import {PAYMENT_METHODS, BOLT} from '../../../api/utils/constants'
import {DEFAULT_SHIPMENT_ID} from '@salesforce/retail-react-app/app/constants'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'
import {
    cleanBoltAddressForOrder,
    isAddressNameValid,
    isAddressPhoneValid,
    normalizeBoltPhoneNumber
} from '../../../utils/address-utils'
import {isFineJewelryExists} from '../../../utils/basket-utils'
import {resolveDefaultShippingMethodId} from '../../../utils/shipping-method-utils'

const BoltWrapper = ({setBoltLoggedIn, setIsBoltPaymentSelected, pwaProducts}) => {
    const {
        app: {commerceAPI}
    } = getConfig()
    const {bolt} = commerceAPI
    const {config} = bolt || {}
    const appOrigin = getAppOrigin()
    const {step, STEPS, goToStep} = useCheckout()
    const {site} = useMultiSite()
    const {getTokenWhenReady} = useAccessToken()
    const [loginComponentMounted, setLoginComponentMounted] = useState(false)
    const boltLoginInitializedRef = useRef(false)
    const boltPaymentInitializedRef = useRef(false)
    const {mutateAsync: updateShippingAddressForShipment} = useShopperBasketsMutation(
        'updateShippingAddressForShipment'
    )
    const updateShippingMethod = useShopperBasketsMutation('updateShippingMethodForShipment')
    const {mutateAsync: updateBillingAddressForBasket} = useShopperBasketsMutation(
        'updateBillingAddressForBasket'
    )
    const {mutateAsync: addPaymentInstrumentToBasket} = useShopperBasketsMutation(
        'addPaymentInstrumentToBasket'
    )
    const {mutateAsync: removePaymentInstrumentFromBasket} = useShopperBasketsMutation(
        'removePaymentInstrumentFromBasket'
    )
    const updateCustomerForBasket = useShopperBasketsMutation('updateCustomerForBasket')
    const {mutateAsync: updateBasket} = useShopperBasketsMutation('updateBasket')
    const updateShipmentForBasketMutation = useShopperBasketsMutation('updateShipmentForBasket')
    const {data: basket, refetch: refetchBasket} = useCurrentBasket()

    const addAccountDetailsToBasket = async (shopperDetails) => {
        sessionStorage.setItem('isBoltLoading', 'true')
        const shipment = basket?.shipments?.[0]
        const basketId = basket?.basketId
        // Normalize like address phones: keep non-US values intact so later
        // isAddressPhoneValid can reject them (do not slice to last 10 chars).
        const profilePhone = normalizeBoltPhoneNumber(shopperDetails?.profile?.phone)
        const boltDefaultAddress = shopperDetails.addresses.find((address) => address.default)
        const boltDefaultPayment = shopperDetails.payment_methods.find(
            (payment) => payment.default && payment.type === BOLT.PAYMENT_METHOD_CARD
        )

        if (boltDefaultAddress && !boltDefaultAddress.phone_number) {
            boltDefaultAddress.phone_number = profilePhone
        }

        const boltBillingAddress = boltDefaultPayment
            ? boltDefaultPayment.billing_address
            : boltDefaultAddress

        if (boltBillingAddress && !boltBillingAddress.phone_number) {
            boltBillingAddress.phone_number = profilePhone
        }

        // Persist after profile-phone injection so session-stored addresses match
        // what is written to the basket (avoids empty-phone "valid" fallback later).
        sessionStorage.setItem('boltShippingAddresses', JSON.stringify(shopperDetails.addresses))

        try {
            if (shopperDetails?.profile?.email) {
                try {
                    await updateCustomerForBasket.mutateAsync({
                        parameters: {basketId: basketId},
                        body: {
                            email: shopperDetails?.profile?.email
                        }
                    })
                } catch (e) {
                    console.warn('Bolt autofill: email update failed', e)
                }
            }

            if (boltDefaultAddress) {
                try {
                    const formattedShippingAddress = cleanBoltAddressForOrder(boltDefaultAddress)
                    await updateShippingAddressForShipment({
                        parameters: {
                            basketId: basketId,
                            shipmentId: shipment?.shipmentId || 'me',
                            useAsBilling: false
                        },
                        body: formattedShippingAddress
                    })

                    const token = await getTokenWhenReady()
                    const {applicableShippingMethods, defaultShippingMethodId} =
                        (await fetchShippingMethods(basket?.basketId, site, token, false)) || {}

                    // Use the same resolver as the shipping-options step so the autofilled
                    // method matches what checkout would have picked - notably free Two-Day
                    // on fine-jewelry baskets. customerSelectedMethod is not passed: autofill
                    // is not the shopper's own choice.
                    const methodId = resolveDefaultShippingMethodId(
                        shipment,
                        applicableShippingMethods,
                        {
                            defaultShippingMethodId,
                            fineJewelryExists: isFineJewelryExists(pwaProducts)
                        }
                    )

                    if (methodId) {
                        await updateShippingMethod.mutateAsync({
                            parameters: {
                                basketId: basketId,
                                shipmentId: shipment.shipmentId
                            },
                            body: {
                                id: methodId
                            }
                        })

                        // Marks the shipping-methods step as satisfied so checkout doesn't
                        // force it open. Safe now that the method above is the resolved
                        // default rather than an arbitrary first entry.
                        localStorage.setItem('customerSelectedShippingMethod', true)
                    }
                } catch (error) {
                    console.warn('Bolt autofill: shipping address/method update failed', error)
                }
            }

            if (boltBillingAddress) {
                try {
                    const formattedBillingAddress = cleanBoltAddressForOrder(boltBillingAddress)
                    delete formattedBillingAddress.phone
                    await updateBillingAddressForBasket({
                        body: formattedBillingAddress,
                        parameters: {basketId: basketId}
                    })
                } catch (error) {
                    console.warn('Bolt autofill: billing address update failed', error)
                }
            }

            if (boltDefaultPayment) {
                try {
                    // The `basket` snapshot predates the shipping/billing updates above, so its
                    // orderTotal has no tax yet. Re-read the basket before pricing the Bolt
                    // payment instrument, otherwise Bolt authorizes for less than the order total.
                    const {data: basketsData} = await refetchBasket()
                    const freshBasket = basketsData?.baskets?.find((b) => b.basketId === basketId)

                    if (!freshBasket) {
                        console.warn('Bolt autofill: could not re-read basket, skipping payment')
                        return
                    }

                    const removals = (freshBasket.paymentInstruments ?? [])
                        .filter((pi) => pi.paymentMethodId !== PAYMENT_METHODS.GIFT_CERTIFICATE)
                        .map((pi) =>
                            removePaymentInstrumentFromBasket({
                                parameters: {
                                    basketId,
                                    paymentInstrumentId: pi.paymentInstrumentId
                                }
                            })
                        )
                    if (removals.length) {
                        await Promise.all(removals)
                    }

                    const creditCardNumber = boltDefaultPayment.last4
                        ? BOLT.CC_MASKED_DIGITS + boltDefaultPayment.last4
                        : ''
                    const network = boltDefaultPayment.network || ''
                    const expMonth = boltDefaultPayment.exp_month || ''
                    const expYear = boltDefaultPayment.exp_year || ''
                    const boltPaymentMethodID = boltDefaultPayment.id || ''
                    const boltPaymentMethodBin = boltDefaultPayment.bin || ''

                    await addPaymentInstrumentToBasket({
                        body: {
                            amount: Math.max(
                                0,
                                (freshBasket.orderTotal || 0) - paidWithGC(freshBasket)
                            ),
                            paymentMethodId: PAYMENT_METHODS.BOLT_PAY,
                            paymentCard: {
                                maskedNumber: creditCardNumber,
                                cardType: network,
                                expirationMonth: parseInt(expMonth),
                                expirationYear: parseInt(expYear)
                            },
                            c_boltPaymentMethodId: boltPaymentMethodID,
                            c_boltPaymentCreditCardNumber: creditCardNumber,
                            c_boltCreateAccount: false, // hardcoded as we don't have bolt create account flow
                            c_boltCardBin: boltPaymentMethodBin,
                            c_boltPaymentCreditCardType: network,
                            c_boltPaymentCreditCardExpirationMonth: String(parseInt(expMonth)),
                            c_boltPaymentCreditCardExpirationYear: String(expYear)
                        },
                        parameters: {
                            basketId: basketId
                        }
                    })

                    await updateBasket({
                        parameters: {basketId},
                        body: {
                            c_boltPaymentMethods: JSON.stringify(shopperDetails.payment_methods)
                        }
                    })
                } catch (error) {
                    console.warn('Bolt autofill: payment instrument update failed', error)
                }
            }
        } finally {
            sessionStorage.removeItem('isBoltLoading')
        }
    }

    const removeBoltDetailsFromBasket = async () => {
        const basketId = basket?.basketId

        await updateShipmentForBasketMutation.mutateAsync({
            parameters: {
                basketId: basketId,
                shipmentId: DEFAULT_SHIPMENT_ID
            },
            body: {
                shippingAddress: {}
            }
        })

        await updateBillingAddressForBasket({
            body: {},
            parameters: {basketId: basketId}
        })

        const basketdata = {
            c_SignUpEmail: '',
            c_boltPaymentMethods: '',
            c_AttentivePhoneNo: ''
        }

        await updateBasket({
            parameters: {basketId: basket?.basketId},
            body: basketdata
        })

        await updateCustomerForBasket.mutateAsync({
            parameters: {basketId: basket.basketId},
            body: {email: ''}
        })

        const boltRemovals = (basket?.paymentInstruments ?? [])
            .filter((pi) => pi.paymentMethodId === PAYMENT_METHODS.BOLT_PAY)
            .map((pi) =>
                removePaymentInstrumentFromBasket({
                    parameters: {
                        basketId: basketId,
                        paymentInstrumentId: pi.paymentInstrumentId
                    }
                })
            )
        if (boltRemovals.length) {
            await Promise.all(boltRemovals)
        }
    }

    let boltCdnUrl = null
    let boltMultiPublishableKey = null
    let boltEnabled = false

    const cookie =
        onClient() &&
        new CookieStorage({
            keySuffix: '',
            sharedContext: !onClient()
        })

    try {
        const jsonConfig = JSON.parse(config)
        boltEnabled = jsonConfig.enabled || false
        boltCdnUrl = boltEnabled ? jsonConfig.boltCdnUrl : null
        boltMultiPublishableKey = boltEnabled ? jsonConfig.boltMultiPublishableKey : null
    } catch (e) {
        console.log(e.message)
    }

    const logout = async () => {
        window.removingBoltDetails = true
        sessionStorage.removeItem('boltOAuthToken')
        sessionStorage.removeItem('boltRefreshToken')
        sessionStorage.removeItem('boltRefreshTokenScope')
        sessionStorage.removeItem('boltOAuthTokenExpire')
        sessionStorage.removeItem('isBoltShopperLoggedIn')
        sessionStorage.removeItem('boltShippingAddresses')
        if (setBoltLoggedIn) {
            setBoltLoggedIn(false)
        }
        if (setIsBoltPaymentSelected) {
            setIsBoltPaymentSelected(false)
        }
        cookie.set('bolt_sfcc_session_logout', 'true')
        setLoginComponentMounted(false)
        await removeBoltDetailsFromBasket()
        window.removingBoltDetails = false
        window.location.reload()
    }

    var resetBoltCreditCardFields = function () {
        document.querySelector('#bolt-cc-token').value = ''
        document.querySelector('#bolt-cc-bin').value = ''
        document.querySelector('#bolt-cc-last-digits').value = ''
        document.querySelector('#bolt-cc-exp').value = ''
        document.querySelector('#bolt-cc-token-type').value = ''
        document.querySelector('#bolt-cc-network').value = ''
        document.querySelector('#bolt-cc-postal').value = ''
    }

    const waitForElement = (selector, timeoutMs = 10000) =>
        new Promise((resolve, reject) => {
            const existing = document.querySelector(selector)
            if (existing) {
                resolve(existing)
                return
            }
            const observer = new MutationObserver(() => {
                const el = document.querySelector(selector)
                if (el) {
                    observer.disconnect()
                    clearTimeout(timeout)
                    resolve(el)
                }
            })
            observer.observe(document.body, {childList: true, subtree: true})
            const timeout = setTimeout(() => {
                observer.disconnect()
                reject(new Error(`${selector} not found within ${timeoutMs}ms`))
            }, timeoutMs)
        })

    useEffect(() => {
        if (!boltEnabled || !basket) {
            return
        }

        // hardcoded until we have datalayer loaded implemented
        window.coreDataLayer = {
            loggedInStatus: false
        }

        const {boltIgniteEnabled, isShopperLoggedIn: isBoltShopperLoggedIn} =
            window.BoltConfig || {}

        window.BoltSelectors = {
            // Sign In page
            signInEmailField: '#login-form-email',
            forgetPasswordButton: '#password-reset',

            // Registration page
            registerEmailField: '#registration-form-email',

            // Checkout page
            checkoutEmailField: '.js-bolt-email',
            checkoutEmailSummary: '.address-summary .email-add',
            editShippingHeader: '.checkout-shipping__heading',
            shippingSummary: '.address-summary',
            addPayment: '.payment-information',
            paymentSummary: '.payment-details'
        }

        window.BoltConfig = {
            publishableKey: boltMultiPublishableKey,
            locale: 'en_US',
            isShopperLoggedIn: isBoltShopperLoggedIn || false,
            boltIgniteEnabled: boltIgniteEnabled || false
        }

        const existingScript = document.querySelector(`script[src="${boltCdnUrl + '/embed.js'}"]`)

        if (!existingScript) {
            const script = document.createElement('script')
            script.src = boltCdnUrl + '/embed.js'
            script.setAttribute('id', 'bolt-embed')
            script.setAttribute('data-publishable-key', boltMultiPublishableKey)
            script.defer = true
            document.body.appendChild(script)
        }
    }, [basket, step])

    useEffect(() => {
        if (!boltEnabled || !basket || step !== STEPS.SHIPPING_ADDRESS) return
        let cancelled = false
        let cleanup = null

        const loginCompleteListener = async (result) => {
            try {
                // if token doesn't exist or is expired, re fetch it
                if (
                    !(
                        sessionStorage.getItem('boltOAuthTokenExpire') &&
                        sessionStorage.getItem('boltOAuthTokenExpire') - new Date().getTime() >
                            BOLT.OAUTH_TOKEN_REFRESH_TIME
                    )
                ) {
                    const fetchBoltTokenURL = appOrigin + '/api/bolt/fetchtoken'
                    const reqBody = {
                        code: result.authorizationCode,
                        scope: result.scope
                    }
                    const data = await fetch(fetchBoltTokenURL, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify(reqBody)
                    })

                    if (!data.error) {
                        const response = await data.json()
                        sessionStorage.setItem('boltOAuthToken', response.access_token)
                        sessionStorage.setItem('boltRefreshToken', response.refresh_token)
                        sessionStorage.setItem(
                            'boltRefreshTokenScope',
                            response.refresh_token_scope
                        )
                        sessionStorage.setItem(
                            'boltOAuthTokenExpire',
                            response.expires_in * 1000 + new Date().getTime()
                        )

                        cookie.set('bolt_sfcc_session_logout', '')
                        window._uxa = window._uxa || []
                        window._uxa.push([
                            'trackDynamicVariable',
                            {key: 'cs_segment', value: 'bolt_user_valid'}
                        ])
                    }
                }

                if (sessionStorage.getItem('boltOAuthToken')) {
                    const fetchBoltCustomerDataURL = appOrigin + '/api/bolt/customerdetails'
                    const data = await fetch(fetchBoltCustomerDataURL, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                            bearerToken: 'Bearer '.concat(sessionStorage.getItem('boltOAuthToken'))
                        })
                    })

                    if (!data.error) {
                        const resp = await data.json()
                        await addAccountDetailsToBasket(resp)
                        sessionStorage.setItem('isBoltShopperLoggedIn', 'true')
                        if (setBoltLoggedIn) {
                            setBoltLoggedIn(true)
                        }
                        if (setIsBoltPaymentSelected) {
                            setIsBoltPaymentSelected(true)
                        }
                        const boltDefaultAddress = resp.addresses?.find(
                            (address) => address.default
                        )
                        const cleanedBoltAddress = cleanBoltAddressForOrder(boltDefaultAddress)
                        const hasValidShippingAddress =
                            isAddressNameValid(cleanedBoltAddress) &&
                            isAddressPhoneValid(cleanedBoltAddress)
                        goToStep(
                            hasValidShippingAddress ? STEPS.REVIEW_ORDER : STEPS.SHIPPING_ADDRESS
                        )
                    }
                }
            } catch (e) {
                console.log(e, 'error')
            }
        }

        const boltReadyPromise = new Promise((resolve) => {
            const timer = setInterval(function () {
                if (window.Bolt == null) {
                    return
                }

                if (!window.Bolt.isInitialized) {
                    const {publishableKey} = window.BoltConfig || {}
                    window.Bolt.initialize(publishableKey, {
                        language: 'en-US'
                    })
                }

                clearInterval(timer)
                resolve()
            }, 500)
        })
        /**
         * mount bolt login status component
         */
        const mountLoginStatusComponent = () => {
            const loginStatusComponent = window.Bolt.create('login_status', {
                listeners: {
                    logout: () => {
                        logout()
                    }
                }
            })

            if (document.querySelector('#login-status') && !loginComponentMounted) {
                loginStatusComponent.mount('#login-status')
                setLoginComponentMounted(true)
            }
        }

        const onLoginComplete = async ({result}) => {
            if (!(result instanceof Error)) {
                loginCompleteListener(result)
            }
        }

        const onLoginFailed = () => {
            sessionStorage.removeItem('isBoltLoading')
        }

        const initBolt = async () => {
            if (boltLoginInitializedRef.current) return

            try {
                const emailField = await waitForElement(window.BoltSelectors.checkoutEmailField)
                await boltReadyPromise

                if (cancelled) return

                const loginModalComponent =
                    window.Bolt.getComponent('login_modal') || window.Bolt.create('login_modal')

                const boltContainer =
                    emailField.closest('.bolt-email-container') || emailField.parentElement
                loginModalComponent.attach(boltContainer)

                mountLoginStatusComponent()

                const unsubLogin = window.Bolt.on('login_complete', onLoginComplete)
                const unsubFailed = window.Bolt.on('login_failed', onLoginFailed)
                unsubscribers.push(unsubLogin, unsubFailed)

                boltLoginInitializedRef.current = true
            } catch (error) {
                console.warn('Bolt login init failed, will retry on next render:', error.message)
            }
        }

        const unsubscribers = []
        initBolt()

        return () => {
            cancelled = true
            unsubscribers.forEach((unsub) => unsub?.())
            boltLoginInitializedRef.current = false
            cleanup?.()
        }
    }, [boltEnabled, basket?.basketId, step])

    useEffect(() => {
        if (!boltEnabled || !basket) return
        if (step < STEPS.PAYMENT) return
        if (!sessionStorage.getItem('isBoltShopperLoggedIn')) return

        const boltReadyPromise = new Promise((resolve) => {
            const timer = setInterval(function () {
                if (window.Bolt == null) {
                    return
                }

                if (!window.Bolt.isInitialized) {
                    const {publishableKey} = window.BoltConfig || {}
                    window.Bolt.initialize(publishableKey, {
                        language: 'en-US'
                    })
                }

                clearInterval(timer)
                resolve()
            }, 500)
        })

        const renderBoltEmbeddedPaymentFields = () => {
            if (document.querySelector('#div-to-inject-field-into').querySelector('iframe')) {
                return
            }
            const paymentComponent = window.Bolt.create('payment_component')
            paymentComponent.mount(document.getElementById('div-to-inject-field-into'))
            paymentComponent.setStyles({
                version: 3,
                '--bolt-input-fontFamily': 'Inter, sans-serif',
                '--bolt-input-fontSize': '1rem',
                '--bolt-input_neutral-backgroundColor': '#fff',
                '--bolt-input_critical_empty_enabled-backgroundColor': '#fff',
                '--bolt-input-borderRadius': '3px',
                '--bolt-input-borderColor': '#ced4da',
                '--bolt-input_focus-borderColor': '#674278',
                '--bolt-input_neutral_focus-borderColor': '#674278',
                '--bolt-input_neutral_empty_focus-borderColor': '#674278',
                '--bolt-input-label-color': '#0a0a0a',
                '--bolt-input-label-fontWeight': '300',
                '--bolt-input_focus-label-color': '#68717e',
                '--bolt-input_focus-label-fontSize': '0.8125rem',
                '--bolt-input_focus-label-fontWeight': '400'
            })
            return paymentComponent
        }

        var getOrCreatePaymentComponent = function () {
            return (
                window.Bolt.getComponent('payment_component') || renderBoltEmbeddedPaymentFields()
            )
        }

        const getToken = async () => {
            await boltReadyPromise
            return getOrCreatePaymentComponent().tokenize()
        }
        const tokenize = (event) => {
            const {resolve, reject} = event.detail || {}
            if (!resolve || !reject) {
                console.warn('checkout:tokenize missing resolve/reject in event.detail')
                return
            }
            // reset credit card fields
            resetBoltCreditCardFields()
            getToken().then(
                function (response) {
                    document.querySelector('#bolt-cc-token').value = response.token
                    document.querySelector('#bolt-cc-bin').value = response.bin
                    document.querySelector('#bolt-cc-last-digits').value = response.last4
                    document.querySelector('#bolt-cc-exp').value = response.expiration
                    document.querySelector('#bolt-cc-token-type').value = response.token_type
                    document.querySelector('#bolt-cc-network').value = response.network
                    document.querySelector('#bolt-cc-postal').value = response.postal_code
                    resolve()
                },
                function (error) {
                    console.warn('Error on getting Bolt token: ', error)
                    document.body.dispatchEvent(
                        new CustomEvent('bolt:tokenize-error', {
                            detail: {message: error?.message || String(error)}
                        })
                    )
                    reject()
                }
            )
        }

        let cancelled = false
        let cleanup = null
        const initBoltPayment = async () => {
            if (boltPaymentInitializedRef.current) return

            await waitForElement('.bolt-card-payment')

            if (cancelled) return
            await boltReadyPromise

            const boltPayCheckbox = document.querySelector('#BOLT_PAY')
            boltPayCheckbox.addEventListener('click', renderBoltEmbeddedPaymentFields)

            if (boltPayCheckbox) {
                boltPayCheckbox.click()
            }

            document.body.addEventListener('checkout:tokenize', tokenize)

            boltPaymentInitializedRef.current = true

            cleanup = () => {
                boltPayCheckbox.removeEventListener('click', renderBoltEmbeddedPaymentFields)
                document.body.removeEventListener('checkout:tokenize', tokenize)
            }
        }

        initBoltPayment()

        return () => {
            cancelled = true
            boltPaymentInitializedRef.current = false
            cleanup?.()
        }
    }, [boltEnabled, basket?.basketId, step])

    useEffect(() => {
        // We'll logout the user if they land on the cart page and the boltOAuthToken session storage is set.
        if (
            sessionStorage.getItem('boltOAuthToken') &&
            window.location.pathname.indexOf('cart') !== -1
        ) {
            logout()
        }
    }, [])

    return (
        <Box onClick={logout} display="none" className="bolt-logout">
            {!isServer && sessionStorage.getItem('isBoltShopperLoggedIn') && (
                <div id="login-status"></div>
            )}
        </Box>
    )
}

BoltWrapper.propTypes = {
    setBoltLoggedIn: PropTypes.any,
    setIsBoltPaymentSelected: PropTypes.any,
    pwaProducts: PropTypes.object
}

export default BoltWrapper
